/**
 * Project Initializer Hook
 * Manages centralized state initialization when switching between projects
 * Prevents state leakage between different projects/lots
 */

import { useEffect, useRef } from 'react'
import { useObjectTypeStore } from '../../../map-editor-shared/store/objectTypeStore'
import { useFloorStore } from '../../../map-editor-shared/store/floorStore'
import { useCsvRuntimeStore } from '../../../map-editor-features/csv/model/csvStore'
import { useProjectStore } from '../../../map-editor-shared/store/projectStore'
import { log, LogCategory } from '../../../map-editor-shared/lib/logger'

export function useProjectInitializer() {
  const currentProjectId = useObjectTypeStore(state => state.currentProjectId)
  const currentLot = useProjectStore(state => state.currentLot)
  const clearAllCsvRuntime = useCsvRuntimeStore((state) => state.clearAllCsvRuntime)
  const clearObjectTypes = useObjectTypeStore(state => state.clearTypes)
  const setCurrentFloor = useFloorStore(state => state.setCurrentFloor)

  const prevProjectIdRef = useRef<number | null>(null)
  const prevLotIdRef = useRef<string | null>(null)
  const isInitialMount = useRef(true)

  useEffect(() => {
    // currentLot is a projectId string, not an object
    const currentLotId = currentLot

    // Skip initialization on first mount
    if (isInitialMount.current) {
      isInitialMount.current = false
      prevProjectIdRef.current = currentProjectId
      prevLotIdRef.current = currentLotId
      return
    }

    // Detect project change
    const projectChanged = currentProjectId !== prevProjectIdRef.current && prevProjectIdRef.current !== null
    const lotChanged = currentLotId !== prevLotIdRef.current && prevLotIdRef.current !== null

    if (projectChanged || lotChanged) {
      log.info(LogCategory.CSV, 'Project/Lot changed, initializing states', {
        projectChanged,
        lotChanged,
        prevProjectId: prevProjectIdRef.current,
        currentProjectId,
        prevLotId: prevLotIdRef.current,
        currentLotId
      })

      // Clear all related states in order
      // 1. Clear CSV data first (most likely to have stale data)
      clearAllCsvRuntime()

      // 2. Clear object types if project changed
      if (projectChanged) {
        clearObjectTypes()
      }

      // 3. Reset current floor if lot changed
      if (lotChanged) {
        setCurrentFloor(null)
      }

      // Log state reset completion
      log.info(LogCategory.CSV, 'State reset completed', {
        csvCleared: true,
        objectTypesReset: projectChanged,
        floorsReset: lotChanged
      })

      // Update refs for next comparison
      prevProjectIdRef.current = currentProjectId
      prevLotIdRef.current = currentLotId
    }
  }, [currentProjectId, currentLot, clearAllCsvRuntime, clearObjectTypes, setCurrentFloor])

  // Return current state for debugging purposes
  return {
    currentProjectId,
    currentLotId: currentLot,
    previousProjectId: prevProjectIdRef.current,
    previousLotId: prevLotIdRef.current
  }
}